import {useMemo, useEffect, useRef, useState} from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { useLocation } from 'react-router-dom'

import {useTable} from 'react-table'

import {DLMSiteApplicationsColumns as UserColumns } from './columns/_columnsUser'
import {DLMSiteApplicationsColumns as ApplicantColumns } from './columns/_columnsApplicant'

import {KTCardBody} from '../../../../../../../_metronic/helpers'

import { orderBy, remove, findIndex } from 'lodash'
import { useDLMSiteApplicationsListContext } from '../components'
import { DLMSiteApplication } from '../../../_models'

import { TableBody, TableHead, TablePagination } from '../../../../../../components/table'
import { useAuth } from '../../../../../auth'

import { SiteApplicationResponseModal as ApplicantResponseModal } from '../../../../../company/doclabel/site-application-response-modal/SiteApplicationResponseModal'
import { SiteApplicationResponseModal as UserResponseModal } from '../../../../../setsco/doclabel/site-application-workflow/SiteApplicationResponseModal'

import { ApplicationTerminateDialog, ApplicationTerminateDialogRef, useApplicationTerminateRequestDialogState, useModalNoteDialogState } from '../../../../../../components/dialogs'
import { editDLMApplication, processDLMApplicationTermination, requestDLMApplicationTermination, submitDLMPhase_1_2 } from '../../../_requests'
import { applyFormSearchFilter, getErrorMessage, processNoteValues } from '../../../../../../components/helpers'
import { DLMApplicationReassignModal } from '../../../../../setsco/doclabel/applications/application-view/reassign-modal/DLMApplicationReassignModal'
import { DOCLabelRequestModal, convertDLMApplication2Request, initDOCLabelApplicationValues } from '../../modals/request-modal'
import { DLMApplicationTerminateModal } from '../../../../../setsco/doclabel/applications/application-terminate-modal/DLMApplicationTerminateModal'

const DLMSiteApplicationsTable = () => {
  const queryClient = useQueryClient();
  const location = useLocation();

  const { initModal, toggleShow } = useModalNoteDialogState(state => state);
  const { initModal : initTerminate, toggleShow: toggleTerminate } = useApplicationTerminateRequestDialogState(state => state);


  const lastpath = useMemo(() => {
    if (! (location && location.pathname)) return '';
    return location.pathname.split('/').pop();
  }, [location]);

  const { sites, state, updateState, currentSite, setCurrentSite, itemIDForDelete, setItemIDForDelete, itemIDForUpdate, setItemIDForUpdate, objectOption1, setObjectOption1, workflow, resubmitData, setResubmitData } = useDLMSiteApplicationsListContext();
  const { page, items_per_page,  nrecs, sort, order } = state;

  const { currentUser, hasRole } = useAuth();

  const modalRef = useRef<ApplicationTerminateDialogRef | null>(null);

  const [show_reassign, showReassign] = useState(false);

  const { data, appID } = useMemo(() => {

    if (! (sites && sites.length > 0)) return { data: [], appID: 0};

    const appID = (sites[0]?.dlmApplication && sites[0].dlmApplication?.id) ? sites[0].dlmApplication.id : 0;
    
    let _rows: Array<DLMSiteApplication> = orderBy(
      applyFormSearchFilter({ list: sites, search: undefined, filter: undefined}), 
      (sort && order) ? [sort] : ['dteUpd','projectName'],
      (sort && order) ? [order] : ['desc','asc'],
    );

    if (currentUser && currentUser?.isSetsco && (! hasRole(['System Admin','DLM Admin']))) {
      remove(_rows, (o: DLMSiteApplication) => (!(o?.assignedTo && o.assignedTo?.id && o.assignedTo.id === currentUser.id)));
    }

    
    const _donerows = remove(_rows, (o: any) => {
      return (o.state === 'Completed' || o.state === 'COMPLETED')
    });

    return {
      data : [..._rows, ..._donerows],
      appID
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites, currentUser, sort, order])

  const show_data = useMemo(() => {
    return data.slice(((page - 1) * items_per_page), page * items_per_page);
  }, [data, page, items_per_page]);

  const columns = useMemo(() => {
    return (currentUser && currentUser.isSetsco) ? UserColumns : ApplicantColumns;
  } , [currentUser]);

  const {getTableProps, getTableBodyProps, headers, headerGroups, rows, prepareRow} = useTable({
    columns,
    data: show_data as Array<DLMSiteApplication>
  })

  // APPLICATION TERMINATION
  const invalidateOnSuccess = () => {
    queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { lastpath }]);
    queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { listmode: 'own'} ]);
    queryClient.invalidateQueries(['DLMAPPLICATION-LOAD', { dlmappid: appID } ]);
  };

  const terminateSiteApplication = useMutation(
    (payload) => requestDLMApplicationTermination(payload),
    {
      onError: (error) => {
        alert(getErrorMessage(error))
      },
      onSuccess: invalidateOnSuccess,
      onSettled: () => {
        setItemIDForDelete(undefined);
      }
    }
  )

  const onConfirmTerminateSiteApplication = async (values: any) => {
    const payload = {
      ...values,
      siteApplicationid: itemIDForDelete
    }
    await terminateSiteApplication.mutateAsync(payload)
  }


  const cancelTerminateSiteApplication = useMutation(
    (payload) => requestDLMApplicationTermination(payload),
    {
      onError: (error) => {
        alert(getErrorMessage(error))
      },
      onSuccess: invalidateOnSuccess,
      onSettled: () => {
        setItemIDForDelete(undefined);
        setItemIDForUpdate(undefined);
      }
    }
  )


  const onConfirmCancelTerminate = async (values: any) => {

    const payload = {
      ...values,
      siteApplicationid: itemIDForDelete,
      cancelRequest: true
    }

    await cancelTerminateSiteApplication.mutateAsync(payload)
  }

  const onConfirmReplyTerminate = async (values: any) => {

    const payload = {
      ...values,
      siteApplicationid: itemIDForUpdate,
      cancelRequest: false
    }

    await cancelTerminateSiteApplication.mutateAsync(payload)
  }

  // USER TERMINATION
  const processTerminateSiteApplication = useMutation(
    (payload: any) => processDLMApplicationTermination(payload),
    {
      onError: (error) => {
        alert(getErrorMessage(error))
      },
      onSuccess: invalidateOnSuccess,
      onSettled: () => {
        setItemIDForDelete(undefined);
      }
    }
  )

  const processTerminationSiteApplicationRequest = async (values: any) => {

    const payload = {
      siteApplicationid: itemIDForDelete,
      approved: (values?.verdict && values.verdict === 'approved') ? true : false,
      reply: (values?.verdict && values.verdict === 'request') ? true : false,
    }

    processNoteValues(values, payload);
    
    if (payload.approved && values?.waive) {
      Object.assign(payload, {waive: true});
    }

    await processTerminateSiteApplication.mutateAsync(payload);
  }

  const closeModal = () => {
    setCurrentSite(null);
  }

  useEffect(()=> {
    const d_cnt = data && data.length ? data.length : 0;
    if (nrecs !== d_cnt) {
        updateState({nrecs : d_cnt});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, nrecs]);

  useEffect(() => {
    if (itemIDForDelete === undefined || (! (sites && sites.length > 0))) {
      if (modalRef.current) modalRef.current.hide();
      toggleShow(false);
      return;
    }

    // User process
    if (currentUser && currentUser?.isSetsco) {
      if (modalRef.current) modalRef.current.show();
      return;
    }

    // Applicant Process
    // @ts-ignore
    const _idx = findIndex(sites, { id : itemIDForDelete })
    if (_idx === -1) return;

    const _site = sites[_idx];
    if (
      (_site?.requestTerminateState !== undefined) &&
      (_site.requestTerminateState === 'REQUEST_TERMINATE_APPLICATION' || _site.requestTerminateState === 'REQUEST_TERMINATE_APPLICATION_REPLY')
    ) {
      initModal({
        title: 'Cancel Site Application Termination Request',
        message: "Are you sure you want to cancel the site application termination request?",
        onOK: onConfirmCancelTerminate,
        onCancel: () => { setItemIDForDelete(undefined); setItemIDForUpdate(undefined); },
        hasNote: 'required',
        hasAttachment: 'yes',
        submitText: "YES",
        cancelText: 'NO',
      });

      toggleShow(true);
    } else {
      initTerminate({
        title: 'TERMINATE SITE APPLICATION',
        message: "Are you sure you want to terminate this site application?",
        onOK: onConfirmTerminateSiteApplication,
      });
      toggleTerminate(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites, itemIDForDelete, currentUser]);

  useEffect(() => {

    if ((! itemIDForUpdate) || (currentUser && currentUser?.isSetsco)) {
      if (modalRef.current) modalRef.current.hide();
      toggleShow(false);
      return;
    }

    initModal({
      title: 'Site Application Termination Information Request',
      message: "Send additional information for site application termination request.",
      onOK: onConfirmReplyTerminate,
      onCancel: () => { setItemIDForDelete(undefined); setItemIDForUpdate(undefined); },
      hasNote: 'required',
      hasAttachment: 'yes',
      submitText: "Submit",
      cancelText: 'Cancel',
    })

    toggleShow(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemIDForUpdate]);

  useEffect(() => {
    showReassign(objectOption1 ? true : false)
  }, [objectOption1])

  return (
  <>
    <KTCardBody className='py-4'>
      <div className='table-responsive'>
        <table
          id='kt_table_dlm_siteapplication'
          className='table align-middle table-row-bordered table-row-gray-300 fs-7 gy-2 dataTable no-footer'
          {...getTableProps()}
        >
          <TableHead {...{headerGroups, sort, order, updateState}} />
          <TableBody        
            getTableBodyProps={getTableBodyProps}
            rows={rows}
            prepareRow={prepareRow}            
            colSpan={headers.length}
          />           
        </table>
      </div>
      <TablePagination isLoading={false} state={state} updateState={updateState} />
    </KTCardBody>


    { (resubmitData !== null) &&
    <DOCLabelRequestModal
      initData={convertDLMApplication2Request(initDOCLabelApplicationValues, resubmitData, currentUser && currentUser?.isSetsco ? 'edit_site' : 'resubmit_site')}
      submitFn={currentUser && currentUser?.isSetsco ? editDLMApplication : submitDLMPhase_1_2}
      onSuccess={() => {
        setResubmitData(null);
        invalidateOnSuccess();
      }}
      cancelForm={() => setResubmitData(null)}
    />
    }

    { currentSite && (
    (currentUser && currentUser?.isSetsco) ? (
    <UserResponseModal site={currentSite} doClose={closeModal} workflow={workflow} />
    ) : (
    <ApplicantResponseModal site={currentSite} doClose={closeModal} workflow={workflow}/>
    )
    )}

    { (currentUser && currentUser?.isSetsco) &&
    <>
    { (itemIDForDelete && itemIDForDelete > 0) &&
    <ApplicationTerminateDialog
      ref={modalRef}
      title="Process Site Application Termination Request"
      message=""
      onOK={processTerminationSiteApplicationRequest}
      onCancel={() => setItemIDForDelete(undefined)}
    />
    }
    {(itemIDForUpdate && itemIDForUpdate > 0) &&
    <DLMApplicationTerminateModal
      appID={appID}
      siteID={itemIDForUpdate}
      onCancel={() => setItemIDForUpdate(undefined)}
      onSuccess={() => {
        invalidateOnSuccess();
        setItemIDForUpdate(undefined);
      }}
    />
    }
    </>
    }

    { (show_reassign && setObjectOption1 && objectOption1) &&
    <DLMApplicationReassignModal
      appID={appID}
      appState="site"
      workflow={workflow}
      assignedId={objectOption1.assignedId}
      role={objectOption1.assignRole}
      doClose={() => {
        showReassign(false);
        setObjectOption1(null);
      }}
      siteApplicationid={objectOption1.siteApplicationid}
    />
    }

    </>
  )
}

export {DLMSiteApplicationsTable}
